/*
 * RescueSeal reference build – <rs-ref-product> component.
 * New file. Every query is scoped to the component instance, so several
 * instances can live on one page. Listeners are removed on disconnect, so
 * Theme Editor section reloads never double-bind.
 */
if (!customElements.get('rs-ref-product')) {
  class RsRefProduct extends HTMLElement {
    connectedCallback() {
      if (this._rsInit) return;
      this._rsInit = true;
      this._abort = new AbortController();
      const signal = this._abort.signal;

      try {
        const json = this.querySelector('[data-rs-ref-variants]');
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.form = this.querySelector('form[data-rs-ref-product-form]');
      this.idInput = this.form ? this.form.querySelector('[data-rs-ref-variant-id]') : null;
      this.errorBox = this.querySelector('[data-rs-ref-error]');
      this.currentVariant = this.variants.find((v) => this.idInput && String(v.id) === this.idInput.value) || null;
      this.busy = false;

      this.initGallery(signal);
      this.initZoom(signal);

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-rs-ref-option]')) this.onOptionChange();
      }, { signal });

      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-rs-ref-qty-step]');
        if (step) this.stepQuantity(Number(step.dataset.rsRefQtyStep));
        const sticky = e.target.closest('[data-rs-ref-sticky-atc]');
        if (sticky) this.submitFromSticky();
      }, { signal });

      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e), { signal });

      this.initSticky();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._stickyObserver) this._stickyObserver.disconnect();
      if (this._fetchAbort) this._fetchAbort.abort();
      this._rsInit = false;
    }

    /* ---------- Gallery ---------- */
    initGallery(signal) {
      this.viewport = this.querySelector('[data-rs-ref-gallery-viewport]');
      if (!this.viewport) return;
      this.slides = Array.from(this.viewport.querySelectorAll('[data-rs-ref-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-rs-ref-thumb]'));
      this.counter = this.querySelector('[data-rs-ref-gallery-current]');
      this.activeIndex = 0;

      const prev = this.querySelector('[data-rs-ref-gallery-prev]');
      const next = this.querySelector('[data-rs-ref-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.activeIndex - 1), { signal });
      if (next) next.addEventListener('click', () => this.goTo(this.activeIndex + 1), { signal });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.rsRefThumb)), { signal }));

      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.activeIndex + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.activeIndex - 1); }
      }, { signal });

      let ticking = false;
      this.viewport.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const w = this.viewport.clientWidth || 1;
          const idx = Math.round(this.viewport.scrollLeft / w);
          if (idx !== this.activeIndex) this.setActive(idx);
        });
      }, { signal, passive: true });

      const start = Number(this.dataset.startIndex || 0);
      if (start > 0) requestAnimationFrame(() => this.goTo(start, 'auto'));
      else this.setActive(0);
    }

    goTo(index, behavior = 'smooth') {
      if (!this.slides || !this.slides.length) return;
      const count = this.slides.length;
      const i = (index + count) % count;
      const slide = this.slides[i];
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : behavior });
      this.setActive(i);
    }

    setActive(i) {
      this.activeIndex = i;
      this.slides.forEach((s, idx) => {
        const active = idx === i;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        // Pause videos that scroll out of view.
        if (!active) s.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) {} });
      });
      this.thumbs.forEach((t, idx) => {
        if (idx === i) {
          t.setAttribute('aria-current', 'true');
          const list = t.closest('.rs-ref-gallery__thumbs');
          if (list) {
            const left = t.offsetLeft - list.clientWidth / 2 + t.clientWidth / 2;
            list.scrollTo({ left, behavior: 'smooth' });
          }
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = String(i + 1);
    }

    goToMedia(mediaId) {
      if (!this.slides || !mediaId) return;
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.goTo(idx);
    }

    /* ---------- Zoom ---------- */
    initZoom(signal) {
      this.zoom = this.querySelector('[data-rs-ref-zoom]');
      if (!this.zoom) return;
      this.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-rs-ref-zoom-open]');
        if (!opener) return;
        this._zoomReturn = opener;
        if (typeof this.zoom.showModal === 'function') this.zoom.showModal();
        else this.zoom.setAttribute('open', '');
        document.documentElement.classList.add('rs-ref-scroll-lock');
        const item = this.zoom.querySelector(`[data-rs-ref-zoom-item="${opener.dataset.rsRefZoomOpen}"]`);
        if (item) item.scrollIntoView({ block: 'start' });
      }, { signal });
      const close = () => {
        if (typeof this.zoom.close === 'function' && this.zoom.open) this.zoom.close();
        else this.zoom.removeAttribute('open');
      };
      this.zoom.querySelector('[data-rs-ref-zoom-close]')?.addEventListener('click', close, { signal });
      this.zoom.addEventListener('click', (e) => { if (e.target === this.zoom) close(); }, { signal });
      this.zoom.addEventListener('close', () => {
        document.documentElement.classList.remove('rs-ref-scroll-lock');
        if (this._zoomReturn) this._zoomReturn.focus();
      }, { signal });
    }

    /* ---------- Variants ---------- */
    getSelectedOptions() {
      return Array.from(this.querySelectorAll('[data-rs-ref-option]')).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        const label = group.querySelector('[data-rs-ref-option-selected]');
        if (checked && label) label.textContent = checked.value;
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.getSelectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
      this.currentVariant = variant;
      this.hideError();

      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant;
      }
      this.updateButtons(variant);
      if (!variant) return;

      if (variant.featured_media) this.goToMedia(variant.featured_media.id);

      if (!window.Shopify || !window.Shopify.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.renderVariantMarkup(variant.id);
    }

    updateButtons(variant) {
      this.querySelectorAll('[data-rs-ref-atc]').forEach((btn) => {
        const label = btn.querySelector('[data-rs-ref-atc-label]');
        let text;
        if (!variant) { btn.disabled = true; text = btn.dataset.labelUnavailable; }
        else if (!variant.available) { btn.disabled = true; text = btn.dataset.labelSoldout; }
        else { btn.disabled = false; text = btn.dataset.labelAdd; }
        if (label && text) label.textContent = text;
      });
    }

    renderVariantMarkup(variantId) {
      if (this._fetchAbort) this._fetchAbort.abort();
      this._fetchAbort = new AbortController();
      const params = new URLSearchParams({ variant: variantId, section_id: this.dataset.sectionId });
      if (this.dataset.templateView) params.set('view', this.dataset.templateView);

      fetch(`${this.dataset.productUrl}?${params.toString()}`, { signal: this._fetchAbort.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('rs-ref-product');
          if (!fresh) return;
          this.querySelectorAll('[data-rs-ref-swap]').forEach((el) => {
            const key = el.getAttribute('data-rs-ref-swap');
            const src = fresh.querySelector(`[data-rs-ref-swap="${CSS.escape(key)}"]`);
            if (src) el.innerHTML = src.innerHTML;
          });
        })
        .catch((e) => { if (e.name !== 'AbortError') console.warn('[RescueSeal] variant refresh failed', e); });
    }

    /* ---------- Quantity ---------- */
    stepQuantity(dir) {
      const input = this.querySelector('.rs-ref-qty__input');
      if (!input) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(min, (Number(input.value) || min) + dir * step));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------- Add to cart ---------- */
    onSubmit(e) {
      if (!window.RSRefCart) return; // native form post as fallback
      e.preventDefault();
      if (this.busy) return;
      if (!this.idInput || !this.idInput.value || (this.currentVariant && !this.currentVariant.available)) return;

      this.busy = true;
      this.setLoading(true);
      this.hideError();

      const formData = new FormData(this.form);
      window.RSRefCart.add({
        formData,
        cartType: this.dataset.cartType,
        cartUrl: this.dataset.cartUrl,
        cartAddUrl: this.dataset.cartAddUrl,
        source: 'product-form',
        variantId: this.idInput.value
      })
        .catch((err) => this.showError(err.message))
        .finally(() => {
          this.busy = false;
          this.setLoading(false);
        });
    }

    submitFromSticky() {
      if (!this.form) return;
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.form.querySelector('[data-rs-ref-atc]') || undefined);
      } else {
        this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }

    setLoading(on) {
      this.querySelectorAll('[data-rs-ref-atc]').forEach((btn) => {
        btn.classList.toggle('is-loading', on);
        btn.setAttribute('aria-busy', on ? 'true' : 'false');
        if (on) btn.disabled = true;
      });
      if (!on) this.updateButtons(this.currentVariant || (this.idInput && this.idInput.value ? { available: true } : null));
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    hideError() {
      if (this.errorBox) { this.errorBox.hidden = true; this.errorBox.textContent = ''; }
    }

    /* ---------- Sticky bar ---------- */
    initSticky() {
      this.sticky = this.querySelector('[data-rs-ref-sticky]');
      const mainBtn = this.form ? this.form.querySelector('[data-rs-ref-atc]') : null;
      if (!this.sticky || !mainBtn || !('IntersectionObserver' in window)) return;
      this._stickyObserver = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) this.sticky.removeAttribute('inert'); else this.sticky.setAttribute('inert', '');
      });
      this._stickyObserver.observe(mainBtn);
    }
  }
  customElements.define('rs-ref-product', RsRefProduct);
}
